import { useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import { Card } from 'src/app/components/Elements/Card';
import { isValidCurrencyText } from 'src/app/utils';
import { formatPrice } from 'src/app/utils/currency';
import { IS_MOBILE } from 'src/consts';

import { convertNumberToValueInCents } from '../utils';

import {
  ActiveEditingContainer,
  CardItemWithoutBottomPadding,
  CurrencyTextInput,
  CurrencyTextInputContainer,
  DollarSignLabel,
  EditTipButton,
  ErrorText,
  GreenText,
  InnerActiveEditingContainer,
  SubmitTipButton,
  TipContainer,
  TipToolTip,
  TotalCostCardItem,
} from './styles';

interface CheckoutPriceBreakdownCardProps {
  isDisabled: boolean;
  showError: boolean;
  onTipSubmitted: (tip: number) => void;
  tip: number;
  total: number;
  showTip: boolean;
  isUserEditingTip: boolean;
  onEditClicked: () => void;
  setIsUserEditingTip: (isUserEditingTip: boolean) => void;
}

export const CheckoutPriceBreakdownCard = ({
  onEditClicked,
  isDisabled,
  showError,
  onTipSubmitted,
  tip,
  total,
  showTip,
  isUserEditingTip,
  setIsUserEditingTip,
}: CheckoutPriceBreakdownCardProps) => {
  const [tipFromUserInput, setTipFromUserInput] = useState<string | null>(tip?.toFixed(2));
  const [hasInvalidTipInput, setHasInvalidTipInput] = useState(false);

  const handleOnTipValueChange = (value: string) => {
    if (isValidCurrencyText(value)) {
      const tipAsFloat = parseFloat(value);
      if (tipAsFloat < 0.0 || tipAsFloat > 999.99) {
        setHasInvalidTipInput(true);
      } else {
        setHasInvalidTipInput(false);
      }
      setTipFromUserInput(value);
    }
  };

  return (
    <Card aria-disabled={isDisabled}>
      {showTip && tipFromUserInput !== null && (
        <CardItemWithoutBottomPadding inactive={isDisabled} noDivider>
          <TipContainer>
            {isUserEditingTip ? (
              <ActiveEditingContainer>
                <GreenText variant={'bodyHeader'} aria-label={'tip'}>
                  <label htmlFor="tip-input">TIP</label>
                </GreenText>
                <InnerActiveEditingContainer>
                  <CurrencyTextInputContainer>
                    <DollarSignLabel>$</DollarSignLabel>
                    <CurrencyTextInput
                      id="tip-input"
                      name="tip"
                      value={tipFromUserInput}
                      onChange={event => handleOnTipValueChange(event.target.value)}
                      type="text"
                      inputMode="decimal"
                    />
                  </CurrencyTextInputContainer>
                  <SubmitTipButton
                    variant={'outline-lg'}
                    // tipFromUserInput is a string and "0" is considered truthy in javascript (0 is falsey)
                    // We multiply the float (tip from user) by 100 because the bff expects it to be in cents so
                    // as an example 7.00 would 700 cents.
                    onClick={() => {
                      if (tipFromUserInput && !hasInvalidTipInput) {
                        setIsUserEditingTip(false);
                        onTipSubmitted(convertNumberToValueInCents(parseFloat(tipFromUserInput)));
                      }
                    }}
                    aria-label={'Click to submit tip'}
                  >
                    Done
                  </SubmitTipButton>
                </InnerActiveEditingContainer>
                {hasInvalidTipInput && (
                  <ErrorText variant={'small'}>
                    The tip you entered exceeds the $999.99 maximum. Please try again.
                  </ErrorText>
                )}
                {showError && !hasInvalidTipInput && (
                  <ErrorText variant={'small'}>Please select ‘Done’ to enter a tip value</ErrorText>
                )}
              </ActiveEditingContainer>
            ) : (
              <>
                <Typography variant={'bodyHeader'} aria-label={'tip'}>
                  TIP
                </Typography>
                <EditTipButton
                  variant={'text-md'}
                  onClick={() => {
                    setIsUserEditingTip(true);
                    onEditClicked();
                  }}
                  aria-label={'Click to edit tip amount'}
                >
                  <GreenText variant={'bodyHeader'}>Edit</GreenText>
                </EditTipButton>
                <TipToolTip anchorText="" header="Tip" body="100% of your tip goes to our couriers." />
              </>
            )}
          </TipContainer>
          {/* On mobile we do not show the tip when the user is editing the tip */}
          {((!isUserEditingTip && IS_MOBILE) || !IS_MOBILE) && (
            <Typography variant={'body'}>{formatPrice(tip)}</Typography>
          )}
        </CardItemWithoutBottomPadding>
      )}
      <TotalCostCardItem total={total} isDisabled={isDisabled} />
    </Card>
  );
};
